import * as React from "react";
import { X, ArrowLeft, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from "../UI/dialog";
import { Button } from "../UI/button";
import { Input } from "../UI/input";
import { Label } from "../UI/label";
import CustomSearchableDropdown from "./CustomSearchableDD";
import { states, countries } from "../Enums/DataEnums";
import { addBusinessAPI } from "../../Modules/Users/SystemChange/SystemChangeAPI";
import { toast } from "sonner";
import { getAuthData } from "../../Utils/AuthUtils";

// Only Indian states are offered here (same restriction ProfileSection.js applies) - non-India
// businesses can still be created, they just won't have a StateID (State is left blank).
const stateOptions = states
  .filter(state => state.CountryID === 1)
  .map(state => ({ value: state.StateID, label: state.State }));

const initialFormData = { companyName: '', city: '', stateID: 0, stateName: '', taxID: '' };

// Lets an already logged-in user spin off a brand new, independent Agency for themselves.
// Mirrors ProfileSection.js's field set (company name / city / state / tax ID) minus PIN and
// POC, which don't apply to a business that doesn't exist yet.
const AddBusinessDialog = ({ open, onClose, onCreated }) => {
  const [formData, setFormData] = React.useState(initialFormData);
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (!open) setFormData(initialFormData);
  }, [open]);

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleStateChange = (stateId) => {
    const selectedState = states.find(state => state.StateID === stateId);
    setFormData(prev => ({
      ...prev,
      stateID: selectedState ? selectedState.StateID : 0,
      stateName: selectedState ? selectedState.State : '',
    }));
  };

  const handleSubmit = async () => {
    if (!formData.companyName.trim()) {
      toast.error('Please enter a business name.');
      return;
    }
    setSubmitting(true);
    try {
      const authData = getAuthData();
      const cc = authData?.CC || authData?.cc || "91";
      const matchedCountry = countries.find(c => String(c.CC) === String(cc));
      const countryID = matchedCountry ? matchedCountry.CountryID : 1;

      const result = await addBusinessAPI({
        companyName: formData.companyName.trim(),
        city: formData.city.trim(),
        stateID: formData.stateID,
        state: formData.stateName,
        taxID: formData.taxID.trim(),
        countryID: countryID,
      });

      if (result?.newBusiness?.AgencyID) {
        toast.success(`${formData.companyName.trim()} has been added.`);
        onCreated(result.newBusiness);
      } else {
        toast.error(result?.message || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      toast.error(error.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-md w-full">
        <DialogHeader className="gap-0 px-3 sm:px-4 md:px-4">
          <div className="sm:hidden flex items-center justify-between w-full h-8">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <DialogClose tabIndex={-1} className="p-0 w-auto h-auto flex-shrink-0 flex justify-center" onClick={onClose}>
                <ArrowLeft className="h-6 w-6" />
                <span className="sr-only">Back</span>
              </DialogClose>
              <DialogTitle className="text-lg font-medium">Add New Business</DialogTitle>
            </div>
          </div>
          <DialogTitle className="hidden sm:block text-lg md:text-xl">Add New Business</DialogTitle>
          <div className="hidden sm:flex items-center gap-2">
            <DialogClose className="flex-shrink-0" tabIndex={-1} onClick={onClose}>
              <X className="h-5 w-5" />
              <span className="sr-only">Close</span>
            </DialogClose>
          </div>
        </DialogHeader>

        <div className="max-h-[70vh] overflow-y-auto px-4">
          <div className="space-y-2 mb-4">
            <Label htmlFor="addBizCompanyName" className="text-sm font-medium" required>
              Business Name
            </Label>
            <Input
              id="addBizCompanyName"
              type="text"
              placeholder="e.g. SahiClub Pvt. Ltd.."
              value={formData.companyName}
              onChange={(e) => handleFieldChange('companyName', e.target.value)}
              className="w-full sahi-input"
              autoComplete="off"
              autoFocus
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="space-y-2">
              <Label htmlFor="addBizCity" className="text-sm font-medium">City</Label>
              <Input
                id="addBizCity"
                type="text"
                placeholder="e.g. Gandhinagar"
                value={formData.city}
                onChange={(e) => handleFieldChange('city', e.target.value)}
                className="w-full sahi-input"
                autoComplete="off"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="addBizState" className="text-sm font-medium">State</Label>
              <CustomSearchableDropdown
                fieldID="addBizState"
                label=""
                placeholder="Select"
                options={stateOptions}
                value={formData.stateID}
                onChange={handleStateChange}
                onSelect={handleStateChange}
                emptyMessage={<>No states available.</>}
                searchEnabled={true}
              />
            </div>
          </div>

          <div className="space-y-2 mb-4">
            <Label htmlFor="addBizTaxID" className="text-sm font-medium">GSTIN / Tax ID</Label>
            <Input
              id="addBizTaxID"
              type="text"
              placeholder="e.g. 24XXXXX0000X1XX"
              value={formData.taxID}
              onChange={(e) => handleFieldChange('taxID', e.target.value)}
              className="w-full sahi-input"
              autoComplete="off"
            />
          </div>
        </div>

        <div className="flex items-center justify-start gap-3 px-4 pb-4 pt-0">
          <Button onClick={handleSubmit} disabled={submitting} className="w-auto">
            <Check className="h-4 w-4" />
            Add Business
          </Button>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            <X className="w-3 h-3" />
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddBusinessDialog;
